import { OrderLinesList, BatchUpdateOrderLines } from "@api/throughShipment/externalOrderApi";
import ExpandIcon from "@assets/expand.png";
import DashBoard from "@component/Common/DashBoard";
import PopupBackground from "@component/PopupBackground";
import { storageTypeOptions } from "@constant/StorageType";
import { convertCurrencyType, Currency } from "@constant/ThroughShipment";
import { externalOrderState, fetchBatchUpdateOrderLines } from "@redux/externalOrderSlice";

import React, { useState } from "react";
import { Table, Typography, Button } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useDispatch, useSelector } from "react-redux";

import * as Style from "./styles";

/**
 * @param orderLinesList - 完整的訂單商品
 * @param dedupeBySkuOrderLinesList - 去除重複 sku的訂單商品
 */

type OrderLinesListWithRequestQty = OrderLinesList & {
  requestQty?: number;
};

type Props = {
  orderLinesList: OrderLinesListWithRequestQty[];
  dedupeBySkuOrderLinesList: OrderLinesListWithRequestQty[];
  onClose: () => void;
  externalOrderId: number;
  requestQtyMap: { [sku: string]: { sku: string; requestQty: number } };
};

const showExpandIconLength = 60;

export default function CheckOrderList(props: Props) {
  const { orderLinesList, onClose, requestQtyMap, externalOrderId, dedupeBySkuOrderLinesList } = props;

  const dispatch = useDispatch();
  const { externalOrderInfo } = useSelector(externalOrderState);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const totalOrderQty = orderLinesList.reduce((acc, cur) => acc + cur.orderedQty, 0);
  const [skuMode, setSkuMode] = useState<boolean>(false);
  const requestQtyMapArr: { sku: string; requestQty: number }[] = Object.values(requestQtyMap);
  const totalRequestQty = requestQtyMapArr.reduce((acc, value) => acc + value.requestQty, 0);

  const onSubmit = () => {
    const result: BatchUpdateOrderLines = {
      lines: dedupeBySkuOrderLinesList.map((item) => {
        return {
          id: item.id,
          batchNumber: item.batchNumber || "",
          effectiveDate: item.effectiveDate || null,
          canAcceptDays: item.canAcceptDays || undefined,
          cost: Number(item.cost),
          quotedPriceCurrency: Number(item.quotedPriceCurrency),
          quotedPriceTwd: Number(item.quotedPriceTwd),
          pcsPerCarton: item.pcsPerCarton,
          orderedQty: item.orderedQty,
          notes: item.notes,
          storageType: item.storageType,
        };
      }),
      revenues: dedupeBySkuOrderLinesList.map((item) => {
        return {
          sku: item.no,
          requestQty: item.requestQty || 0,
        };
      }),
    };
    const payload = {
      externalOrder: externalOrderId,
      orderLines: result,
    };

    dispatch(fetchBatchUpdateOrderLines(payload));
    onClose();
  };

  const clickExpandIcon = (rowId: number) => {
    if (expandedRows.includes(rowId)) {
      setExpandedRows(expandedRows.filter((id) => id !== rowId));
    } else {
      setExpandedRows(expandedRows.concat(rowId));
    }
  };

  const skuModeColumns: ColumnsType<OrderLinesListWithRequestQty> = [
    {
      key: "no",
      dataIndex: "no",
      title: "商品品號",
    },
    {
      key: "name",
      dataIndex: "name",
      title: "品名",
      width: 250,
    },
    {
      key: "requestQty",
      dataIndex: "requestQty",
      title: "單品總需求量(pcs)",
      render: (value?: number) => value || 0,
    },
    {
      key: "orderedQty",
      dataIndex: "orderedQty",
      title: "單品總出貨量(pcs)",
      render: (value: any, data: any) => {
        const type = value !== data.requestQty ? "danger" : undefined;
        return <Typography.Text type={type}>{value}</Typography.Text>;
      },
    },
  ];

  const columns: ColumnsType<OrderLinesList> = [
    {
      key: "id",
      dataIndex: "id",
      title: "編號",
    },
    {
      key: "no",
      dataIndex: "no",
      title: "商品品號",
    },
    {
      key: "sku",
      dataIndex: "sku",
      title: "SKU",
      width: "5%",
    },
    {
      key: "name",
      dataIndex: "name",
      title: "品名",
      width: 250,
    },
    {
      key: "storageType",
      dataIndex: "storageType",
      title: "倉別",
      width: "5%",
      render: (value) => storageTypeOptions.find((type) => type.key === value)?.name || "-",
    },
    {
      key: "effectiveDateBatch",
      dataIndex: "effectiveDateBatch",
      title: "效期＿批號",
      width: 130,
    },
    {
      key: "canAcceptDays",
      dataIndex: "canAcceptDays",
      title: "允收天數",
      width: 100,
    },
    {
      key: "cost",
      dataIndex: "cost",
      title: "商品成本(TWD)",
      width: 150,
    },
    {
      key: "quotedPriceCurrency",
      dataIndex: "quotedPriceCurrency",
      title: `訂單商品外幣報價(${convertCurrencyType(externalOrderInfo!.currency)})`,
      width: 200,
    },
    {
      key: "quotedPriceTwd",
      dataIndex: "quotedPriceTwd",
      title: "訂單商品報價(TWD)",
      width: 170,
      render: (value, data) => {
        const isTWD = externalOrderInfo!.currency === Currency.TWD;
        const type = isTWD && value !== data.quotedPrice ? "danger" : undefined;
        return <Typography.Text type={type}>{value}</Typography.Text>;
      },
    },
    {
      key: "orderedQty",
      dataIndex: "orderedQty",
      title: "總數量(pcs)",
      width: 150,
    },
    {
      key: "notes",
      dataIndex: "notes",
      title: "備註",
      width: 250,
      render: (data, value) => {
        const isExpanded = data?.length && !expandedRows.includes(value.id);

        const getCharLength = () => {
          // 若匹配中文字符則佔寬+2,非中文+1
          const regex = /[\u4e00-\u9fa5]/;
          let count = 0;
          for (let i = 0; i < data?.length; i += 1) {
            const char = data[i];
            if (regex.test(char)) {
              count += 2;
            } else {
              count += 1;
            }
          }
          return count;
        };

        const showExpandIcon = getCharLength() >= showExpandIconLength;

        const cutString = (str: string, showLength: number) => {
          // 以中文字的寬度來判斷，若小於 showLength 可以直接 return
          if (str.length * 2 <= showLength) {
            return str;
          }
          let strLength = 0;
          let showText = "";
          for (let i = 0; i < str.length; i += 1) {
            const char = str.charAt(i);
            const charCode = str.charCodeAt(i);
            // Unicode 編碼值大於 128 代表中文字符 -> 字符的寬度是 2 個字符位置。
            const charWidth = charCode > 128 ? 2 : 1;
            if (strLength + charWidth <= showLength) {
              showText += char;
              strLength += charWidth;
            }
          }
          if (strLength < str.length * 2) {
            showText += "...";
          }
          return showText;
        };

        return (
          <div>
            <Style.NotesWrapper>
              {!isExpanded ? (
                <Style.Notes isExpanded={isExpanded} id={`${value.id}-note`}>
                  {data}
                </Style.Notes>
              ) : (
                cutString(data, 50)
              )}
            </Style.NotesWrapper>
            {showExpandIcon && <Style.ExpandIconImg src={ExpandIcon} onClick={() => clickExpandIcon(value.id)} />}
          </div>
        );
      },
    },
  ];

  const dashBoardData = [
    {
      name: "總需求量(pcs)",
      value: totalRequestQty,
    },
    {
      name: "出貨總數量(pcs)",
      value: totalOrderQty,
      status: totalOrderQty !== totalRequestQty && "error",
    },
  ];

  return (
    <PopupBackground close={onClose} fixed>
      <Style.Wrapper>
        <Style.HeaderWrapper>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Style.Title>確認出貨商品</Style.Title>
            <DashBoard dataset={dashBoardData} />
          </div>
          <Button type="primary" onClick={() => setSkuMode(!skuMode)}>
            {skuMode ? "切換為出貨商品細節" : "切換為出貨商品數量總覽"}
          </Button>
        </Style.HeaderWrapper>
        {skuMode ? (
          <Table
            columns={skuModeColumns}
            dataSource={dedupeBySkuOrderLinesList}
            pagination={false}
            scroll={{ x: "max-content" }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={orderLinesList}
            pagination={false}
            scroll={{ x: "max-content", y: 300 }}
          />
        )}
        <Style.CustomRow justify="end" align="middle">
          <Style.CustomButton marginRight="12px" onClick={onClose}>
            取消
          </Style.CustomButton>
          <Style.CustomButton type="primary" disabled={orderLinesList.length === 0} onClick={onSubmit}>
            確認
          </Style.CustomButton>
        </Style.CustomRow>
      </Style.Wrapper>
    </PopupBackground>
  );
}
